import React, { useEffect, useRef } from 'react';
import { useHoverStylesClassName } from '../hooks/useHoverStylesClassName';
import { cls } from '../View/utils';
import styles from './Routes.module.scss';
import { Outlet } from 'react-router-dom';
import GlobalTestingCodes from '../GlobalTestingCodes';
import ErrorConsumer from '../error/ErrorConsumer';
import { useProfilesLocalStorage } from '../ProfilesLocalStorageContext';
import { useYleConsentSdk } from '../hooks/useYleConsentSdk';
import { getVrsDevice } from '../analytics/analytics-utils';
import usePromiseWithErrorHandling from '../hooks/usePromiseWithErrorHandling';
import { LoadingOverlay } from '../common/loading/LoadingOverlay';
import { useEventListener } from '../hooks/useEventListener';
import { useReloadAreena } from './reload';
import { useAppFeaturesContext } from '../contexts/AppFeaturesContext/AppFeaturesContext';
import { useAuthContext } from '../contexts/AuthContext/AuthContext';
import { ErrorName } from '../error/errors';
import Sidebar from '../Sidebar/Sidebar';
import { useFetchAppData } from '../../service/areena-api/hooks/useFetchAppData';
import { IntroVideo } from '../IntroVideo/IntroVideo';
import { introVideoShownStorageKey } from '../environment-prod';
import { hbbTvMode, ps4Mode } from '../../common/types';
import { useSessionStorage } from '../hooks/useSessionStorage';
import useDeviceRedirects from './redirects/useDeviceRedirects';
import { useErrorRedirects } from './redirects/useErrorRedirects';
import ContextMenu from '../ContextMenu/ContextMenu';

const shouldShowIntroVideo = (mode: string) =>
  mode !== hbbTvMode && mode !== ps4Mode;

const REFRESH_APP_AFTER_MS = 120 * 60 * 1000; // 2 hours

const RootElement: React.FC = () => {
  const hoverStyleClassname = useHoverStylesClassName();
  const { device, mode } = useAppFeaturesContext();
  const { setProfileByUser, profiles } = useProfilesLocalStorage();

  const [introVideoShown, setIntroVideoShown] = useSessionStorage<boolean>(
    introVideoShownStorageKey,
    false
  );

  const [isSessionInitialized, setIsSessionInitialized] = React.useState(false);
  const { initConsentSdk: initConsentSdkAsync } = useYleConsentSdk();
  const { fetchUser, user } = useAuthContext();
  const appData = useFetchAppData();

  const appOnBackgroundSince = useRef<number | undefined>(undefined);

  const { run: initConsentSdk } =
    usePromiseWithErrorHandling(initConsentSdkAsync);

  const getLoggedInUserRunner = usePromiseWithErrorHandling(
    (vrsDevice: string, ...args: Parameters<typeof fetchUser>) =>
      fetchUser(...args)
        .then(user => {
          if (user) {
            initConsentSdk(vrsDevice);
          }
        })
        .finally(() => {
          setIsSessionInitialized(true);
        }),
    [ErrorName.AUTH_ERROR, ErrorName.EXPIRED_SESSION_ERROR]
  );

  useEffect(() => {
    const vrsDevice = getVrsDevice(device);
    getLoggedInUserRunner.run(vrsDevice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      const profile = profiles.find(p => p.userId === user?.userId);
      setProfileByUser(user, profile?.cookie);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const reloadAreena = useReloadAreena();

  useEventListener(
    'visibilitychange',
    () => {
      if (document.visibilityState === 'hidden') {
        appOnBackgroundSince.current = Date.now();
      } else {
        if (
          appOnBackgroundSince.current &&
          Date.now() - appOnBackgroundSince.current > REFRESH_APP_AFTER_MS
        ) {
          reloadAreena(user);
        }
      }
    },
    document
  );

  useDeviceRedirects();
  useErrorRedirects();

  return (
    <ErrorConsumer>
      {!introVideoShown && shouldShowIntroVideo(mode) ? (
        <IntroVideo setIntroVideoShown={setIntroVideoShown} />
      ) : (
        <GlobalTestingCodes>
          <div
            className={cls({
              [styles.routes]: true,
              [`${hoverStyleClassname}`]: !!hoverStyleClassname,
            })}
          >
            {isSessionInitialized ? (
              <>
                <Sidebar appData={appData} />
                <ContextMenu />
                <Outlet />
              </>
            ) : (
              <LoadingOverlay isVisible={true} />
            )}
          </div>
        </GlobalTestingCodes>
      )}
    </ErrorConsumer>
  );
};

export default RootElement;
